import React, { useCallback, useEffect, useState } from "react";
import { LayoutDashboard, Users, Car, Map, Calendar, BarChart3, Plug, Search, Plus, Star, Pencil, Power, Check, X } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const navItems = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Conductores", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users },
  { to: "/admin/integraciones", label: "Integraciones", icon: Plug },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];
const bottomNav = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Choferes", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];

const CATEGORY_LABELS = { sedan: "Sedán", suv: "SUV", van: "Van" };
const EMPTY_FORM = { full_name: "", email: "", phone: "", password: "" };
const EMPTY_EDIT = { full_name: "", email: "", phone: "" };

const AdminDrivers = () => {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [rowSaving, setRowSaving] = useState(false);
  const [rowError, setRowError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/conductores");
      setDrivers(data);
    } catch {
      setError("No se pudo cargar la lista de conductores.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitNewDriver = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      // POST /api/auth/admin/users solo crea la cuenta (nombre, correo,
      // teléfono, contraseña) con rol conductor. DNI, licencia y vehículo
      // los completa el propio conductor la primera vez que entra a su
      // panel -- el admin no los puede cargar acá, por eso el formulario
      // no los pide (a diferencia del mockup original de Emergent).
      await api.post("/api/auth/admin/users", { ...form, role: "conductor" });
      setOpenForm(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo crear el conductor.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (d) => {
    setEditingId(d.user.id);
    setEditForm({ full_name: d.user.full_name, email: d.user.email, phone: d.user.phone || "" });
    setRowError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setRowError("");
  };

  const saveEdit = async (userId) => {
    setRowSaving(true);
    setRowError("");
    try {
      const { data } = await api.patch(`/api/admin/conductores/cuenta/${userId}`, editForm);
      setDrivers((prev) => prev.map((d) => (d.user.id === userId ? data : d)));
      setEditingId(null);
    } catch (err) {
      setRowError(err.response?.data?.detail || "No se pudo guardar los cambios.");
    } finally {
      setRowSaving(false);
    }
  };

  const toggleStatus = async (d) => {
    setRowError("");
    try {
      const { data } = await api.patch(`/api/admin/conductores/cuenta/${d.user.id}/estado`, { is_active: !d.user.is_active });
      setDrivers((prev) => prev.map((x) => (x.user.id === d.user.id ? data : x)));
    } catch (err) {
      setRowError(err.response?.data?.detail || "No se pudo cambiar el estado del conductor.");
    }
  };

  const filtered = drivers.filter((d) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      d.user.full_name.toLowerCase().includes(q) ||
      (d.profile?.license_number || "").toLowerCase().includes(q) ||
      d.vehicles.some((v) => v.plate.toLowerCase().includes(q))
    );
  });

  return (
    <PanelShell role="Admin" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} userMeta="Administrador" testId="admin-drivers">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Flota LEP</p>
            <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Conductores</h1>
          </div>
          <button
            onClick={() => setOpenForm(!openForm)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
            data-testid="new-driver-btn"
          >
            <Plus size={14} /> Nuevo conductor
          </button>
        </div>

        {error && (
          <p className="mb-4 text-xs text-red-400 font-body" data-testid="admin-drivers-error">
            {error}
          </p>
        )}
        {rowError && (
          <p className="mb-4 text-xs text-red-400 font-body" data-testid="admin-drivers-row-error">
            {rowError}
          </p>
        )}

        {openForm && (
          <form onSubmit={submitNewDriver} className="mb-6 bg-[#1C1D20] border border-[#C9A227]/40 rounded-xl p-6" data-testid="new-driver-form">
            <h3 className="font-display font-bold text-lg uppercase tracking-wide mb-1">Alta de conductor</h3>
            <p className="text-xs text-[#A0A0A5] mb-6">
              Crea la cuenta de acceso. El conductor completa DNI, licencia y vehículo la primera vez que entra a su panel.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Nombre completo" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} required />
              <Input label="Correo" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
              <Input label="Teléfono / WhatsApp" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              <Input label="Contraseña temporal" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required />
            </div>
            <div className="mt-6 flex items-center gap-3 justify-end">
              <button type="button" onClick={() => setOpenForm(false)} className="px-5 py-2.5 border border-[#2E2F33] text-[#A0A0A5] hover:border-[#C9A227] uppercase tracking-wider font-display text-xs rounded-lg">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 bg-[#C9A227] disabled:opacity-60 text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
                data-testid="save-driver-btn"
              >
                {saving ? "Guardando…" : "Guardar conductor"}
              </button>
            </div>
          </form>
        )}

        <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#2E2F33] flex items-center gap-3">
            <div className="flex-1 flex items-center gap-2 bg-[#0D0E10] border border-[#2E2F33] focus-within:border-[#C9A227] rounded-lg px-3.5 py-2.5">
              <Search size={14} className="text-[#C9A227]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre, licencia o placa..."
                className="flex-1 bg-transparent outline-none text-sm text-[#F5F5F5] font-body"
                data-testid="drivers-search"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="drivers-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display border-b border-[#2E2F33]">
                  <th className="text-left px-5 py-3 font-normal">Conductor</th>
                  <th className="text-left px-5 py-3 font-normal">Vehículo activo</th>
                  <th className="text-left px-5 py-3 font-normal">Licencia</th>
                  <th className="text-left px-5 py-3 font-normal">Rutas</th>
                  <th className="text-left px-5 py-3 font-normal">Rating</th>
                  <th className="text-left px-5 py-3 font-normal">Perfil</th>
                  <th className="text-left px-5 py-3 font-normal">Estado</th>
                  <th className="text-left px-5 py-3 font-normal">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {!loading &&
                  filtered.map((d) => {
                    const activeVehicle = d.vehicles.find((v) => v.is_active);
                    const isEditing = editingId === d.user.id;
                    return (
                      <tr key={d.user.id} className="border-b border-[#2E2F33] hover:bg-[#0D0E10]/50 transition" data-testid={`driver-row-${d.user.id}`}>
                        <td className="px-5 py-4">
                          {isEditing ? (
                            <div className="space-y-1.5">
                              <input
                                value={editForm.full_name}
                                onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))}
                                className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-1.5 text-xs text-[#F5F5F5] font-body outline-none"
                                data-testid={`driver-edit-name-${d.user.id}`}
                              />
                              <input
                                value={editForm.phone}
                                onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                                className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-1.5 text-xs text-[#F5F5F5] font-body outline-none"
                                data-testid={`driver-edit-phone-${d.user.id}`}
                              />
                              <input
                                value={editForm.email}
                                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                                className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-1.5 text-xs text-[#F5F5F5] font-body outline-none"
                                data-testid={`driver-edit-email-${d.user.id}`}
                              />
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-[#C9A227]/15 border border-[#C9A227]/40 flex items-center justify-center text-[#C9A227] font-display font-bold text-xs">
                                {d.user.full_name.split(" ").map((s) => s[0]).join("").slice(0, 2)}
                              </div>
                              <div>
                                <p className="font-body">{d.user.full_name}</p>
                                <p className="text-[10px] text-[#A0A0A5]">{d.user.phone || d.user.email}</p>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4 font-body text-[#A0A0A5]">
                          {activeVehicle
                            ? `${activeVehicle.brand} ${activeVehicle.model} · ${activeVehicle.plate} (${CATEGORY_LABELS[activeVehicle.category] || activeVehicle.category})`
                            : d.vehicles.length > 0
                              ? "Sin vehículo activo"
                              : "—"}
                        </td>
                        <td className="px-5 py-4 font-mono text-xs text-[#A0A0A5]">{d.profile?.license_number || "—"}</td>
                        <td className="px-5 py-4 font-body">{d.routes.length}</td>
                        <td className="px-5 py-4">
                          {d.rating_average != null ? (
                            <span className="flex items-center gap-1 text-[#C9A227]">
                              <Star size={12} fill="currentColor" /> {d.rating_average.toFixed(1)} ({d.rating_count})
                            </span>
                          ) : (
                            <span className="text-[#A0A0A5]">Sin reseñas</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          {d.profile ? (
                            <span className="text-[10px] uppercase text-[#C9A227] tracking-wider font-display">Completo</span>
                          ) : (
                            <span className="text-[10px] uppercase text-[#A0A0A5] tracking-wider font-display">Pendiente</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <StatusBadge status={d.user.is_active ? "Activo" : "Descanso"} />
                        </td>
                        <td className="px-5 py-4">
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => saveEdit(d.user.id)}
                                disabled={rowSaving}
                                className="p-1.5 text-emerald-400 hover:bg-emerald-500/10 rounded-md disabled:opacity-60"
                                title="Guardar"
                                data-testid={`driver-save-${d.user.id}`}
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="p-1.5 text-[#A0A0A5] hover:bg-[#2E2F33] rounded-md"
                                title="Cancelar"
                                data-testid={`driver-cancel-${d.user.id}`}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => startEdit(d)}
                                className="p-1.5 text-[#A0A0A5] hover:text-[#C9A227] hover:bg-[#C9A227]/10 rounded-md"
                                title="Editar"
                                data-testid={`driver-edit-${d.user.id}`}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => toggleStatus(d)}
                                className={`p-1.5 rounded-md ${d.user.is_active ? "text-red-400 hover:bg-red-500/10" : "text-emerald-400 hover:bg-emerald-500/10"}`}
                                title={d.user.is_active ? "Desactivar" : "Activar"}
                                data-testid={`driver-toggle-status-${d.user.id}`}
                              >
                                <Power size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-8 text-center text-xs text-[#A0A0A5] font-body">
                      No hay conductores que coincidan con la búsqueda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PanelShell>
  );
};

const Input = ({ label, ...props }) => (
  <div>
    <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">{label}</label>
    <input {...props} className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none transition-colors placeholder:text-[#5A5B60]" />
  </div>
);

export default AdminDrivers;
